import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import {
  getPrices,
  createCheckout,
  createPortal,
} from '@/lib/api/subscription';
import { useSubscription } from '@/contexts/useSubscription';
import { getSubscriptionError } from '@/lib/errors';
import { captureFriendlyError } from '@/lib/sentry';
import { track } from '@/lib/analytics';
import BillingToggle from '@/components/pricing/BillingToggle';
import OnDarkButton from '@/components/pricing/OnDarkButton';
import { PREMIUM_FEATURES } from '@/components/pricing/premiumFeatures';
import type { FriendlyError } from '@/lib/errors';
import type { ApiError } from '@/lib/api/client';
import type { PriceCadence, PricesResponse } from '@/types/database';

const ease = [0.22, 1, 0.36, 1] as const;

export default function PricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const canceled = searchParams.get('canceled') === '1';
  const { isPremium, cadence: activeCadence } = useSubscription();

  const stateFrom = (location.state as { from?: string } | null)?.from;
  const queryFrom = searchParams.get('from');
  const fromCandidate = stateFrom ?? queryFrom ?? '/home';
  const fromPath =
    fromCandidate.startsWith('/') && !fromCandidate.startsWith('//')
      ? fromCandidate
      : '/home';

  const [prices, setPrices] = useState<PricesResponse | null>(null);
  const [userSelected, setUserSelected] = useState<PriceCadence | null>(null);
  const [error, setError] = useState<FriendlyError | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    const source = fromPath.startsWith('/') ? fromPath.slice(1) || 'home' : fromPath;
    track('pricing_viewed', { source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    getPrices()
      .then((data) => {
        if (active) setPrices(data);
      })
      .catch((err) => {
        if (!active) return;
        const apiError = err as ApiError;
        const friendly = getSubscriptionError(apiError.code || 'prices_load_failed');
        captureFriendlyError(err, friendly);
        setError(friendly);
      });
    return () => {
      active = false;
    };
  }, []);

  const selected: PriceCadence =
    userSelected ?? (isPremium ? activeCadence ?? 'annual' : 'annual');

  const selectedPrice =
    prices && (selected === 'annual' ? prices.yearly : prices.monthly);
  const savingsPct = prices?.yearly.savings_percentage ?? 0;

  const handleCheckout = async () => {
    if (!selectedPrice) return;
    setCheckoutLoading(true);
    setError(null);
    track('checkout_started', {
      plan: selected === 'annual' ? 'annual' : 'monthly',
      has_onboarding_discount: false,
    });
    try {
      const origin = window.location.origin;
      const cancelUrl = new URL('/pricing', origin);
      cancelUrl.searchParams.set('canceled', '1');
      if (fromPath !== '/home') cancelUrl.searchParams.set('from', fromPath);
      const { checkout_url } = await createCheckout({
        priceId: selectedPrice.id,
        successUrl: `${origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: cancelUrl.toString(),
      });
      window.location.href = checkout_url;
    } catch (err) {
      const apiError = err as ApiError;
      const friendly = getSubscriptionError(apiError.code || 'checkout_failed');
      captureFriendlyError(err, friendly);
      setError(friendly);
      setCheckoutLoading(false);
    }
  };

  const handleManage = async () => {
    setPortalLoading(true);
    setError(null);
    track('billing_portal_opened');
    try {
      const origin = window.location.origin;
      const { portal_url } = await createPortal(`${origin}/settings`);
      window.location.href = portal_url;
    } catch (err) {
      const apiError = err as ApiError;
      const friendly = getSubscriptionError(apiError.code || 'portal_failed');
      captureFriendlyError(err, friendly);
      setError(friendly);
      setPortalLoading(false);
    }
  };

  const isSelectedActive =
    isPremium && activeCadence !== null && activeCadence === selected;

  return (
    <div className="relative min-h-screen bg-cream md:bg-gradient-to-br md:from-cream md:via-cream md:to-cream-warm overflow-hidden">
      <TopBar onBack={() => navigate(fromPath)} />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[700px] hidden md:block"
        style={{
          background:
            'radial-gradient(ellipse 900px 500px at 20% 10%, rgba(212,163,115,0.18), transparent 60%)',
        }}
      />
      <div className="relative mx-auto w-full max-w-2xl px-6 py-10 md:px-8 md:py-16 lg:py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease }}
          className="text-center mb-8 md:mb-10"
        >
          <h1 className="font-display italic font-light text-5xl md:text-7xl text-text-primary leading-[1.05] mb-4">
            Keep practicing.
          </h1>
          <p className="font-sans text-base md:text-lg text-text-secondary max-w-lg mx-auto">
            Unlimited mock interviews, full practice mode, and every future
            feature.
          </p>
        </motion.div>

        {canceled && (
          <div className="mb-6 rounded-2xl bg-warning-light border border-warning/20 px-4 py-3 text-center">
            <p className="font-sans text-sm text-text-primary">
              Checkout canceled — no charge was made.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-2xl bg-error-light border border-error/20 px-4 py-3 text-left">
            <p className="font-display font-semibold text-base text-error leading-tight">
              {error.title}
            </p>
            <p className="font-sans text-sm text-error/80 mt-1">
              {error.message}
            </p>
          </div>
        )}

        {!prices && !error && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {prices && selectedPrice && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1, ease }}
              className="flex justify-center mb-6"
            >
              <BillingToggle
                selected={selected}
                onSelect={setUserSelected}
                savingsPct={savingsPct}
                activeCadence={isPremium ? activeCadence : null}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18, ease }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-dark text-white p-7 md:p-10 shadow-lg"
            >
              <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-white/[0.06] pointer-events-none" />
              <div className="absolute -bottom-20 -left-12 w-52 h-52 rounded-full bg-white/[0.04] pointer-events-none" />

              <div className="relative">
                <div className="mb-7">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-semibold text-6xl md:text-7xl text-white leading-none">
                      {selectedPrice.display_price}
                    </span>
                    <span className="font-sans text-base text-white/70">
                      {selected === 'annual' ? 'per year' : 'per month'}
                    </span>
                  </div>
                  <p className="font-sans text-sm text-white/80 mt-2 h-5">
                    {selected === 'annual'
                      ? `${prices.yearly.monthly_equivalent} / month — billed annually`
                      : 'Billed every month'}
                  </p>
                </div>

                <ul className="space-y-3 mb-8">
                  {PREMIUM_FEATURES.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check
                        size={18}
                        className="text-white flex-shrink-0 mt-0.5"
                      />
                      <span className="font-sans text-sm md:text-base text-white/95">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {isPremium ? (
                  isSelectedActive ? (
                    <OnDarkButton
                      label="Manage Subscription"
                      onClick={handleManage}
                      loading={portalLoading}
                    />
                  ) : (
                    <OnDarkButton
                      label="Switch in Billing Portal"
                      onClick={handleManage}
                      loading={portalLoading}
                    />
                  )
                ) : (
                  <OnDarkButton
                    label="Get Premium"
                    onClick={handleCheckout}
                    loading={checkoutLoading}
                  />
                )}
              </div>
            </motion.div>

            <p className="text-center font-sans text-xs text-text-muted mt-6">
              Cancel anytime from your billing portal.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function TopBar({ onBack }: { onBack: () => void }) {
  return (
    <div className="relative z-10 border-b border-border/60 bg-card/40 backdrop-blur-sm">
      <div className="mx-auto w-full max-w-6xl px-6 md:px-8 lg:px-12 h-14 flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 font-sans text-sm text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-text-muted">
          Upgrade
        </p>
        <span aria-hidden className="w-[52px]" />
      </div>
    </div>
  );
}

